import { useSegment } from '@repo/poly-hooks/segment';
import { useCurrentDevice } from '@repo/shared-kit/hooks';
import type { KycProvider } from '@repo/api-kit/queries/due-diligence/kyc';
import { createContext, useContext, useMemo, useCallback } from 'react';
import type { PropsWithChildren } from 'react';

export interface KycProviderProps {
  /** Organization's legal country code (e.g. `"FR"`, `"DE"`).
   * Used as market parameter for API calls when `isRegisterFlow` is true,
   * and to select the appropriate loading UI (IDnow for `"DE"`, Fourthline otherwise). */
  legalCountry: string;
  /** Current membership ID. Used only for analytics tracking and Sentry error context. */
  membershipId?: string;
  /** Current user ID. Used only for analytics tracking. */
  userId?: string;
  /** Prefix for all Segment tracking events (e.g. `"verification"`, `"onboarding_partner"`).
   * Events are emitted as `${trackingPrefix}_kyc_started`, `${trackingPrefix}_kyc_succeeded`, etc. */
  trackingPrefix: string;
  /** When provided, the success screen shows a link to this URL instead of calling `onComplete`.
   * Used when the KYC flow is embedded and needs to redirect externally on completion. */
  completionRedirectUrl?: string;
  /** Global error handler called on unexpected errors (ErrorBoundary, SMS failures).
   * Allows the parent app to log or report errors centrally. */
  onError?: (error: Error) => void;
  /** Called when the user presses back in a provider's UI (IDnow or Fourthline). */
  onBack?: () => void;
  /** Main completion callback. Called when KYC is skipped (already completed) or
   * when the user finishes the verification flow. */
  onComplete: () => void;
  /** Whether the user holds a German residence permit. Passed to the IDnow provider
   * to adjust identity verification requirements. */
  hasGermanResidencePermit: boolean;
  /** Enables register-flow-specific behavior:
   * - API calls include `legalCountry` as market parameter
   * - KYC is only shown when process status is `pending_liveness`
   * - All other statuses are skipped (handled elsewhere in the register flow) */
  isRegisterFlow?: boolean;
}

interface KycContextValue extends Omit<KycProviderProps, 'onComplete'> {
  onComplete: (provider: keyof typeof KycProvider) => void;
}

const KycContext = createContext<KycContextValue | null>(null);

export function KycContextProvider({ children, ...props }: PropsWithChildren<KycProviderProps>) {
  const { isMobileBrowser } = useCurrentDevice();
  const segment = useSegment();
  const { onComplete, membershipId, userId, trackingPrefix } = props;

  const handleOnComplete = useCallback(
    (provider: keyof typeof KycProvider) => {
      segment.track(`${trackingPrefix}_kyc_succeeded`, {
        membership_id: membershipId,
        provider,
        is_webview: isMobileBrowser,
        user_id: userId,
        is_new_kyc: true,
      });

      onComplete();
    },
    [isMobileBrowser, membershipId, userId, trackingPrefix, segment, onComplete]
  );

  const value = useMemo<KycContextValue>(
    () => ({
      legalCountry: props.legalCountry,
      membershipId: props.membershipId,
      userId: props.userId,
      trackingPrefix: props.trackingPrefix,
      completionRedirectUrl: props.completionRedirectUrl,
      onError: props.onError,
      onBack: props.onBack,
      onComplete: handleOnComplete,
      hasGermanResidencePermit: props.hasGermanResidencePermit,
      isRegisterFlow: props.isRegisterFlow,
    }),
    [
      props.legalCountry,
      props.membershipId,
      props.userId,
      props.trackingPrefix,
      props.completionRedirectUrl,
      props.onError,
      props.onBack,
      props.hasGermanResidencePermit,
      props.isRegisterFlow,
      handleOnComplete,
    ]
  );

  return <KycContext.Provider value={value}>{children}</KycContext.Provider>;
}

export function useKycContext(): KycContextValue {
  const context = useContext(KycContext);
  if (!context) {
    throw new Error('useKycContext must be used within a KycProvider');
  }
  return context;
}
